import jsQR from 'jsqr';
import { DecodedQRResult } from '@/types/qr';
import { parseDecodedText } from './payloads';
import { analyzeDecodedUrl } from '@/lib/security/sanitizer';

// Type declaration for BarcodeDetector API
declare global {
  interface Window {
    BarcodeDetector?: {
      new (options?: { formats: string[] }): {
        detect: (image: ImageBitmapSource) => Promise<Array<{ rawValue: string }>>;
      };
      getSupportedFormats: () => Promise<string[]>;
    };
  }
}

/**
 * Decodes a QR code from an HTMLImageElement or HTMLCanvasElement.
 */
export async function decodeImageSource(
  imageSource: HTMLImageElement | HTMLCanvasElement | ImageBitmap
): Promise<DecodedQRResult | null> {
  let rawText: string | null = null;

  // 1. Try native BarcodeDetector API if available
  if ('BarcodeDetector' in window && window.BarcodeDetector) {
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      const barcodes = await detector.detect(imageSource);
      if (barcodes.length > 0 && barcodes[0].rawValue) {
        rawText = barcodes[0].rawValue;
      }
    } catch {
      // Fall through to jsQR
    }
  }

  // 2. Fallback to jsQR via 2D Canvas ImageData extraction
  if (!rawText) {
    let canvas: HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D | null;

    if (imageSource instanceof HTMLCanvasElement) {
      canvas = imageSource;
      ctx = canvas.getContext('2d');
    } else {
      canvas = document.createElement('canvas');
      canvas.width = imageSource.width;
      canvas.height = imageSource.height;
      ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (ctx) {
        ctx.drawImage(imageSource, 0, 0, canvas.width, canvas.height);
      }
    }

    if (ctx) {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth'
      });
      if (code && code.data) {
        rawText = code.data;
      }
    }
  }

  if (!rawText) return null;

  const { type, parsedPayload } = parseDecodedText(rawText);
  const securityAnalysis = analyzeDecodedUrl(rawText);

  return {
    rawText,
    detectedType: type,
    parsedPayload,
    timestamp: Date.now(),
    source: 'image-file',
    securityAnalysis
  };
}

/**
 * Decodes a user-uploaded File object (PNG, JPEG, WebP, SVG).
 */
export async function decodeFile(file: File): Promise<DecodedQRResult | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const result = await decodeImageSource(img);
        resolve(result);
      };
      img.onerror = () => resolve(null);
      img.src = reader.result as string;
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Decodes video frames from a live <video> element.
 */
export function decodeVideoFrame(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement
): DecodedQRResult | null {
  if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
    return null;
  }

  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) return null;

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(video, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);

  const code = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: 'dontInvert'
  });

  if (code && code.data) {
    const { type, parsedPayload } = parseDecodedText(code.data);
    const securityAnalysis = analyzeDecodedUrl(code.data);

    return {
      rawText: code.data,
      detectedType: type,
      parsedPayload,
      timestamp: Date.now(),
      source: 'camera',
      securityAnalysis
    };
  }

  return null;
}
